import { parseAgendaActionRequest, type AgendaCreateDraft } from "../../supabase/functions/_shared/agendaActionDomain";
import { canonicalTaskSnapshot } from "../../supabase/functions/_shared/taskActionDomain";
import { acknowledgeAgendaAction, executeReviewedAgendaAction, readAgendaPromotionTask, readPendingAgendaAction, invokeAgendaAction, newAgendaOperationId, parseAgendaRecord, AgendaExecutorError } from "@/lib/agendaExecutor";
import { publishAgendaRefresh } from "@/lib/agendaChanges";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";
import { localEventDayKey } from "@/lib/agendaEventRange";
import { taskDueDay, taskWeekRange } from "@/lib/taskDates";
import { supabase } from "@/integrations/supabase/client";
import { isSyncedTask, createConfirmedTask, updateConfirmedTask, deleteConfirmedTask, TASK_SELECT_FIELDS, type TaskSnapshot } from "@/lib/taskMutations";
import type { CreateTaskInput } from "./useTasks";
import { publishTaskRefresh, subscribeTaskChanges, taskRevision } from "@/lib/taskChanges";

/**
 * Frente C Fase 1 — Agenda Tasks card hook (12/05/26).
 *
 * Reads `public.tasks` filtered to the current ISO week (Mon→Sun), only
 * rows with `done=false` AND `due_date IS NOT NULL` (no-deadline tasks
 * live in the /tasks complete view, NOT in the agenda card per Phase 1
 * spec).
 *
 * Schema (verified via Supabase MCP 12/05):
 *   priority text NOT NULL CHECK IN ('Critical','Important','Normal')
 *   due_date timestamptz NULL
 *   done boolean NOT NULL
 *   source text NOT NULL
 *   external_id text NULL  — used to link a task to a promoted
 *                            agenda_events.id (bidirectional, task and
 *                            event coexist)
 *   client_action_id text NULL — idempotency key, mirrors agenda_events
 *
 * Cache pattern mirrors useFitnessSnapshot / useNutritionSnapshot: a
 * module-level singleton with 60s TTL + exported invalidate helper.
 * Confirmed writes update mounted task views; revisions invalidate cached reads.
 *
 * Sort order (applied client-side post-fetch, custom priority rank since
 * the priority column is text):
 *   due_date ASC, priority Critical→Important→Normal, created_at ASC.
 */

export type Priority = "Critical" | "Important" | "Normal";

export interface AgendaTask {
  id: string;
  user_id: string;
  title: string;
  priority: Priority;
  due_date: string | null;
  time_label: string | null;
  done: boolean;
  completed_at: string | null;
  client_action_id: string | null;
  external_id: string | null;
  created_at: string | null;
  // Provider provenance ('google_tasks' etc.) — gates delete (rule 30/06).
  source?: string | null;
}

interface CacheEntry {
  data: AgendaTask[];
  expiresAt: number;
  revision: number;
  weekStart: string;
}

const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000;

export function invalidateAgendaTasksCache(userId?: string): void {
  if (userId) cache.delete(userId);
  else cache.clear();
}

const PRIORITY_RANK: Record<Priority, number> = {
  Critical: 1,
  Important: 2,
  Normal: 3,
};

function sortTasks(rows: AgendaTask[]): AgendaTask[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    const ad = a.due_date ?? "";
    const bd = b.due_date ?? "";
    if (ad !== bd) return ad.localeCompare(bd);
    const ar = PRIORITY_RANK[a.priority] ?? 9;
    const br = PRIORITY_RANK[b.priority] ?? 9;
    if (ar !== br) return ar - br;
    const ac = a.created_at ?? "";
    const bc = b.created_at ?? "";
    return ac.localeCompare(bc);
  });
  return copy;
}

export function useAgendaTasks(userId: string | undefined) {
  const [tasksToday, setTasksToday] = useState<AgendaTask[]>([]);
  const [tasksWeek, setTasksWeek] = useState<AgendaTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const requestRef = useRef(0);
  const loadedRef = useRef(false);
  const ownerRef = useRef(userId);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const authOwnerRef = useRef<string | null | undefined>();

  const splitByScope = useCallback((rows: AgendaTask[]) => {
    const todayYmd = localEventDayKey(new Date().toISOString());
    const todayRows: AgendaTask[] = [];
    const weekRows: AgendaTask[] = [];
    for (const r of rows) {
      if (!r.due_date) continue; // no-deadline tasks live in /tasks only
      if (taskDueDay(r.due_date) === todayYmd) todayRows.push(r);
      else weekRows.push(r);
    }
    setTasksToday(todayRows);
    setTasksWeek(weekRows);
  }, []);

  const fetchTasks = useCallback(async (force = false) => {
    const request = ++requestRef.current;
    const checked = Date.now();
    if (!userId) {
      splitByScope([]);
      setCheckedAt(null);
      setLoading(false);
      return;
    }
    setLoadError(false);

    let authenticated;
    try { authenticated = await supabase.auth.getUser(); }
    catch (cause) {
      if (request === requestRef.current) { console.error("[useAgendaTasks] session failed:", cause); setLoadError(true); setLoading(false); }
      return;
    }
    const { data: { user }, error: authError } = authenticated;
    if (request !== requestRef.current) return;
    if (authError || user?.id !== userId) {
      loadedRef.current = false; splitByScope([]); setLoadError(true); setLoading(false); return;
    }
    authOwnerRef.current = user.id;

    const week = taskWeekRange();
    const cached = cache.get(userId);
    if (cached && cached.expiresAt > Date.now() && cached.revision === taskRevision(userId) && cached.weekStart === week.firstDay && !force) {
      loadedRef.current = true;
      splitByScope(cached.data);
      setCheckedAt(checked);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const rows: AgendaTask[] = [];
      let cursor: string | undefined;
      for (let page = 0; ; page++) {
        if (page >= 200) throw new Error("TASK_READ_INCOMPLETE");
        let query = supabase.from("tasks").select(TASK_SELECT_FIELDS).eq("user_id", userId).eq("done", false)
          .not("due_date", "is", null).gte("due_date", week.start).lte("due_date", week.end)
          .order("id", { ascending: true }).limit(500);
        if (cursor) query = query.gt("id", cursor);
        const { data, error } = await query;
        if (request !== requestRef.current) return;
        if (error) throw error;
        if (!data?.length) break;
        for (const row of data) {
          if (row.user_id !== userId || typeof row.id !== "string" || (cursor && row.id <= cursor)) throw new Error("TASK_READ_INCOMPLETE");
          cursor = row.id; rows.push(row as AgendaTask);
        }
      }

      loadedRef.current = true;
      const sorted = sortTasks(rows);
      cache.set(userId, { data: sorted, expiresAt: Date.now() + CACHE_TTL_MS, revision: taskRevision(userId), weekStart: week.firstDay });
      splitByScope(sorted);
      setCheckedAt(checked);
    } catch (error) {
      console.error("[useAgendaTasks] fetch threw:", error);
      if (request === requestRef.current) setLoadError(true);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [userId, splitByScope]);

  useEffect(() => {
    mountedRef.current = true;
    const stop = () => { mountedRef.current = false; generationRef.current++; requestRef.current++; };
    ownerRef.current = userId;
    generationRef.current++;
    // Never show a previous account's tasks while a new account loads.
    loadedRef.current = false;
    setCheckedAt(null);
    splitByScope([]);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (authOwnerRef.current === next) return;
      authOwnerRef.current = next;
      generationRef.current++;
      requestRef.current++;
      loadedRef.current = false;
      invalidateAgendaTasksCache(userId);
      setCheckedAt(null);
      splitByScope([]);
      setLoading(next === userId);
      if (next === userId) queueMicrotask(() => { if (mountedRef.current && ownerRef.current === userId) void fetchTasks(true); });
    });
    const unsubscribe = subscribeTaskChanges(change => {
      if (change.userId !== userId || authOwnerRef.current !== userId) return;
      invalidateAgendaTasksCache(userId);
      if ("refresh" in change) { void fetchTasks(true); return; }
      if (!loadedRef.current) { void fetchTasks(true); return; }
      requestRef.current += 1;
      setLoading(false);
      const task: AgendaTask | null = change.task ? { ...change.task, external_id: change.task.external_id ?? null,
        time_label: change.task.time_label ?? null, completed_at: change.task.completed_at ?? null, client_action_id: change.task.client_action_id ?? null } : null;
      const now = new Date();
      const due = taskDueDay(task?.due_date);
      const week = taskWeekRange(now);
      const inWeek = !!task && !task.done && !!due && due >= week.firstDay && due <= week.lastDay;
      const today = inWeek && due === localEventDayKey(now.toISOString());
      const update = (rows: AgendaTask[], include: boolean) => sortTasks([
        ...rows.filter(row => row.id !== change.id), ...(include && task ? [task] : []),
      ]);
      setTasksToday(prev => update(prev, !!today));
      setTasksWeek(prev => update(prev, !!inWeek && !today));
    });
    void fetchTasks();
    return () => { unsubscribe(); subscription.unsubscribe(); stop(); };
  }, [fetchTasks, userId, splitByScope]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading, error: loadError,
    enabled: !!userId && authOwnerRef.current === userId, refresh: () => fetchTasks(true) });

  const requireUser = useCallback(() => {
    if (!userId || ownerRef.current !== userId || authOwnerRef.current !== userId) throw new Error("TASK_SESSION_UNAVAILABLE");
    return userId;
  }, [userId]);

  const writeGuard = useCallback((uid: string) => {
    const generation = generationRef.current;
    return () => { if (!mountedRef.current || generation !== generationRef.current || uid !== ownerRef.current || uid !== authOwnerRef.current) throw new Error("TASK_SESSION_UNAVAILABLE"); };
  }, []);
  const addTask = useCallback(async (input: Omit<CreateTaskInput, "priority"> & { priority?: Priority }): Promise<AgendaTask | null> => {
    const uid = requireUser();
    const task = await createConfirmedTask({ ...input, priority: input.priority ?? "Normal" }, uid, writeGuard(uid));
    return { ...task, external_id: task.external_id ?? null, time_label: task.time_label ?? null, completed_at: task.completed_at ?? null, client_action_id: task.client_action_id ?? null };
  }, [requireUser, writeGuard]);

  const toggleDone = useCallback(async (taskId: string, done: boolean, snapshot?: TaskSnapshot): Promise<void> => {
    const uid = requireUser();
    await updateConfirmedTask(taskId, { done }, uid, !done, snapshot, writeGuard(uid));
  }, [requireUser, writeGuard]);

  const updateTask = useCallback(async (
    taskId: string, patch: Partial<Pick<AgendaTask, "title" | "priority" | "due_date">>, snapshot?: TaskSnapshot,
  ): Promise<void> => {
    const uid = requireUser();
    await updateConfirmedTask(taskId, patch, uid, undefined, snapshot, writeGuard(uid));
  }, [requireUser, writeGuard]);

  const deleteTask = useCallback(async (taskId: string, snapshot?: TaskSnapshot): Promise<void> => {
    const target = [...tasksToday, ...tasksWeek].find(task => task.id === taskId);
    if (target && isSyncedTask(target)) throw new Error("SYNCED_TASK_DELETE_BLOCKED");
    const uid = requireUser();
    await deleteConfirmedTask(taskId, uid, snapshot, writeGuard(uid));
  }, [tasksToday, tasksWeek, requireUser, writeGuard]);

  const linkToEvent = useCallback(async (taskId: string, eventId: string, snapshot?: TaskSnapshot): Promise<void> => {
    const uid = requireUser();
    await updateConfirmedTask(taskId, { external_id: eventId }, uid, undefined, snapshot, writeGuard(uid));
  }, [requireUser, writeGuard]);
  const promoteToEvent = useCallback(async (taskId: string, draft: AgendaCreateDraft, snapshot: TaskSnapshot, operationId = newAgendaOperationId()) => {
    const uid = requireUser(), assert = writeGuard(uid); assert();
    if (!snapshot) throw new AgendaExecutorError("incomplete_task_snapshot");
    const reviewed = canonicalTaskSnapshot(snapshot, uid);
    if (reviewed.task.id !== taskId) throw new AgendaExecutorError("invalid_task_snapshot");
    let request = parseAgendaActionRequest({ ...structuredClone(draft), action: "promote_task", task_id: taskId, expected_task_snapshot: reviewed, operation_id: operationId, confirmed: true });
    const pending = await readPendingAgendaAction(uid); assert();
    if (pending) {
      const previous = parseAgendaActionRequest(pending.body);
      const stable = (value: unknown): unknown => Array.isArray(value) ? value.map(stable) : value && typeof value === "object" ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, stable(item)])) : value;
      const intent = (value: typeof request) => JSON.stringify(stable(Object.fromEntries(Object.entries(value).filter(([key]) => !["operation_id", "idempotency_key"].includes(key)))));
      if (previous.mirror_context || intent(request) !== intent(previous)) throw new AgendaExecutorError("agenda_pending_action");
      request = structuredClone(pending.body) as unknown as typeof request;
    }
    const { entry, response } = await executeReviewedAgendaAction(uid, request, assert); assert();
    const task = await readAgendaPromotionTask(taskId, uid, assert); assert();
    const current = await invokeAgendaAction({ action: "get", event_id: request.event_id }, uid, assert); assert();
    const event = parseAgendaRecord(current.event);
    if (task.external_id !== request.event_id || !event || event.id !== request.event_id || event.user_id !== uid) throw new AgendaExecutorError("agenda_read_unconfirmed");
    await acknowledgeAgendaAction(entry, assert); assert();
    publishTaskRefresh(uid); assert(); publishAgendaRefresh(uid); assert();
    return { response, event, task };
  }, [requireUser, writeGuard]);

  return {
    tasksToday,
    tasksWeek,
    loading,
    loadError,
    refetch: () => fetchTasks(true),
    addTask,
    toggleDone,
    updateTask,
    deleteTask,
    linkToEvent,
    promoteToEvent,
  };
}
